/**
 * caseBadges.js — Shared status badge logic
 * v206.0: Extracted from CasesListScreen so HomeScreen + CasesList stay in sync.
 * Mirrors MD Panel caseBadges.js color families.
 *
 * Usage:
 *   import { getStatusBadge } from '../utils/caseBadges'
 *   const badge = getStatusBadge(caseItem, t)
 *   // badge => { label, bg, color }
 */

import { resolveShareStatus, shareCodeOf } from './caseShareState'
import { hasPendingReschedule } from './pendingReschedule' // PROD-921
import { requestDeclined, requestCancelledByPhysician } from './declinedRequest' // PROD-983, PROD-1137

export function getStatusBadgeMap(t) {
  // v221.2.0: canonical brand palette only.
  //   - Active/in-progress workflow states → Ice Blue + Navy (#E9F8FF/#15354F)
  //   - Light Blue badge (#A7D9FC/#15354F) for "shared/visit prep" — earlier in pipeline
  //   - Completed/finalized → brand Green family (already canonical)
  //   - Observation/archived → slate neutrals (already canonical)
  //   - Draft → muted amber retained (canonical "needs your attention" — patient
  //     should know they have a draft they haven't shared yet)
  // Removed: orange/amber on in_follow_up (was the screenshot bug); violet on
  // claimed; indigo on scheduled; cyan on in_progress. (Teal retired v242.101.0.)
  return {
    draft:            { label: t('casesList.draft'),                                bg: '#FEF3C7', color: '#92400E' },
    ready_to_share:   { label: t('casesList.readyToShare'),                         bg: '#A7D9FC', color: '#15354F' },
    shared:           { label: t('casesList.shared'),                               bg: '#E9F8FF', color: '#15354F' },
    submitted:        { label: t('casesList.visitPrep') || 'Case Report',            bg: '#E9F8FF', color: '#15354F' },
    claimed:          { label: t('casesList.claimed'),                              bg: '#E9F8FF', color: '#15354F' },
    scheduled:        { label: t('casesList.scheduled'),                            bg: '#E9F8FF', color: '#15354F' },
    in_progress:      { label: t('casesList.inProgress'),                           bg: '#E9F8FF', color: '#15354F' },
    checked_in:       { label: 'Checked In',                                        bg: '#E9F8FF', color: '#15354F' },
    completed:        { label: t('casesList.finalized'),                            bg: '#D5EFD3', color: '#15803D' },
    in_follow_up:     { label: t('casesList.inFollowup') || 'In Follow-Up',         bg: '#E9F8FF', color: '#15354F' },
    in_observation:   { label: t('casesList.observation') || 'Observation',         bg: '#F1F5F9', color: '#475569' },
    archived:         { label: t('casesList.archived'),                             bg: '#F1F5F9', color: '#475569' },
  }
}

/**
 * Returns { label, bg, color } for a given case.
 * Mirrors CasesListScreen.getStatusBadge — backward-compat shims included.
 */
export function getStatusBadge(caseItem, t) {
  const STATUS_BADGES = getStatusBadgeMap(t)
  // ★★ v242.98.0 (TF b37 #2) — a case shared BY CODE keeps the database status
  //   `ready_to_share` and gains a share_code, so reading `caseItem.status` raw
  //   told the patient "Ready to Share" about a case they had already shared.
  //   resolveShareStatus is the one place that knows this; do not inline it.
  const status = resolveShareStatus(caseItem.status, caseItem)
  const isDescribeComplete = caseItem.describeComplete || caseItem.describe?.completed

  if (STATUS_BADGES[status]) {
    // Shim: ready_to_share if describe done but API hasn't promoted yet.
    // ★ v242.98.0 — a draft that already carries a share code is SHARED, not
    //   ready to share; the shim would otherwise re-introduce the exact defect
    //   resolveShareStatus just corrected, one branch further down.
    if (status === 'draft' && isDescribeComplete && !shareCodeOf(caseItem)) {
      return { ...STATUS_BADGES.ready_to_share, label: t('cases.statusReadyToShare') || 'Ready to Share' }
    }
    // Shim: scheduled if appointment confirmed but API hasn't promoted yet
    // ★ PROD-921: not while a row waits in pending_reschedule — the case column still says
    //   'confirmed' then, but the new time is a pending request (no shim, as for 'pending').
    if (status === 'claimed' && (caseItem.appointmentStatus === 'confirmed' || caseItem.appointment_status === 'confirmed')
      && !hasPendingReschedule(caseItem)) {
      return { ...STATUS_BADGES.scheduled, label: t('cases.statusScheduled') || 'Scheduled' }
    }
    // ★ PROD-983: a declined held reschedule of a confirmed initial visit leaves the case column
    //   'scheduled' (the API's decline writes no case status); with the request declined nothing is
    //   scheduled, so the case reads as with the doctor (the 'claimed' badge), never "Scheduled".
    // ★ PROD-1137: the same for a visit the physician cancelled (the API moves the case to 'claimed';
    //   this covers a case whose status write did not land).
    if (status === 'scheduled' && (requestDeclined(caseItem.appointmentRequest || caseItem.appointment_request)
      || requestCancelledByPhysician(caseItem.appointmentRequest || caseItem.appointment_request))) {
      return { ...STATUS_BADGES.claimed, label: t('cases.statusClaimed') }
    }
    const badge = STATUS_BADGES[status]
    const i18nMap = {
      draft: t('cases.statusDraft'),
      ready_to_share: t('cases.statusReadyToShare'),
      shared: t('cases.statusShared'),
      submitted: t('cases.statusVisitPrep') || 'Case Report',
      claimed: t('cases.statusClaimed'),
      scheduled: t('cases.statusScheduled'),
      in_progress: t('cases.statusInProgress'),
      checked_in: 'Checked In',
      completed: t('cases.statusFinalized'),
      in_follow_up: t('casesList.inFollowup') || 'In Follow-Up',
      in_observation: t('casesList.observation') || 'Observation',
      archived: t('caseHome.archived'),
    }
    return { ...badge, label: i18nMap[status] || badge.label }
  }

  return { label: status || 'Unknown', bg: '#F1F5F9', color: '#475569' }
}
